<template>
    <div class="frame-common" :class="getMainFrameClasses" ref="frame">
        <header class="frame-common__header">
            <div class="frame-common__header__title">
                <router-link :to="{name: 'index'}" class="frame-common__header__title__link">
                    <div class="frame-common__header__title__link__logo">
                        <SemmelweisLogoSvg/>
                    </div>
                    <div class="frame-common__header__title__link__main">{{ $t('header.title') }}</div>
                    <div class="frame-common__header__title__link__sub">{{ $t('header.projectTitle') }}{{ projectTitleSuffix }}</div>
                </router-link>
            </div>
            <div v-if="$user.isLoggedIn()" class="frame-common__header__user">
                <div class="frame-common__header__user__okt">
                    <div class="frame-common__header__user__okt__name">{{ $user.data.okt_identifier ? $t('labels.oktIdentifier') : null}}</div>
                    <div class="frame-common__header__user__okt__email">{{$user.data.okt_identifier ?? null}}</div>
                </div>
                <div class="frame-common__header__user__info">
                    <div class="frame-common__header__user__info__name" v-if="accountInfoShouldBeVisible">{{$user.data.name}}</div>
                    <div class="frame-common__header__user__info__email" v-if="accountInfoShouldBeVisible">{{$user.data.email}}</div>
                </div>
                <div class="frame-common__header__user__avatar">
                    <AvatarSvg/>
                </div>
                <div class="frame-common__header__user__logout">
                    <div v-if="$inactivityObserver.running" class="frame-common__header__user__logout__timer">{{ $inactivityObserver.expireAt.formatted }}</div>
                    <router-link v-if="$user.isLoggedIn()" :to="{name: 'logout'}" :title="$t('navigation.logout')"/>
                </div>
            </div>
        </header>
        <div class="frame-common__navigation">
            <Navigation/>
            <LanguageSelector/>
        </div>
        <div class="frame-common__content">
            <router-view class="frame-common__content__view" v-if="!$user.isLoggedIn()" v-slot="{ Component }">
                <Transition>
                    <component :is="Component" :class="getContentViewClasses" class="page"/>
                </Transition>
            </router-view>
            <router-view class="frame-common__content__view page" v-else :class="getContentViewClasses"></router-view>
        </div>
        <footer>
            <Footer/>
        </footer>
        <Moveable v-for="[symbol, item] in timeoutPopups" :key="symbol" :x="item.position.x" :y="item.position.y">
            <div class="timeout-popup">
                <div class="timeout-popup__content">{{item.content}}</div>
                <div class="timeout-popup__cross" @click="item.callback()"></div>
            </div>
        </Moveable>
    </div>
</template>

<script>
import Navigation from "../../components/Navigation.vue";
import Button from "primevue/button";
import SemmelweisLogoSvg from "@media/images/vector/sote-logo.svg";
import AvatarSvg from "@media/images/vector/profile.svg";
import Footer from "../../components/Footer.vue";
import LanguageSelector from "../../components/LanguageSelector.vue";
import Moveable from "../../components/Moveable.vue";

export default {
    components: {Moveable, LanguageSelector, Footer, Button, Navigation, SemmelweisLogoSvg, AvatarSvg},

    data() {return {
        timeoutPopups: new Map(),
        mouseObserver: {
            x: null,
            y: null
        }
    };},

    watch: {
        '$route'(to, from) {
            if(this.$user.isLoggedIn() && this.$user.data.request_new_password) {
                this.$router.replace({
                    name: 'passwordChange'
                });
            }
        }
    },

    mounted() {
        this.$refs.frame.addEventListener("mousemove", this.mouseObserverCallback);
        this.$refs.frame.addEventListener("mouseenter", this.mouseObserverCallback);
        this.$refs.frame.addEventListener("mouseleave", this.mouseObserverCallback);

        if(this.$user.isLoggedIn() && this.$user.data.request_new_password) {
            this.$nextTick(() => {
                this.$router.push({name: 'passwordChange.forced'});
            });
        }
    },

    provide() {return {
        triggerRequestSentToServerTimeoutPopup: (label, params, delay = 3000) => {
            this.$nextTick(() => { // $nextTick is important here
                this.addTimeoutPopup(this.$t(label ?? 'labels.requestSentToServer', params), delay);
            })
        },
    };},

    methods: {
        mouseObserverCallback(event) {
            this.mouseObserver.x = event.pageX;
            this.mouseObserver.y = event.pageY;
        },

        getThemeName() {
            // Or attach a select menu for the theming and return the proper value from that.
            return "-styling-theme-default";
        },

        addTimeoutPopup(string, delay = 0, symbol) {
            if(this.timeoutPopups.has(symbol)) {
                return;
            }

            let item = {
                symbol: symbol ?? Symbol(),
                content: string,
                timeoutId: delay ? setTimeout(() => {
                    item.callback();
                }, delay) : null,
                callback: () => {
                    if(item.timeoutId) {
                        clearTimeout(item.timeoutId);
                    }

                    this.timeoutPopups.delete(item.symbol);
                },
                position: {
                    ...this.mouseObserver
                }
            };

            this.timeoutPopups.set(item.symbol, item);
        },
    },

    computed: {
        projectTitleSuffix() {
            switch(this.$configuration.get('vueJs.frameInjectedClass')) {
                case '-test-environment':
                    return ' (Teszt)';
                default:
                    return null;
            }
        },

        getMainFrameClasses() {
            return [
                this.$user.isLoggedIn() ? '-user' : '-guest',
                this.getThemeName(),
                this.$configuration.get('vueJs.frameInjectedClass'),
            ];
        },

        getContentViewClasses() {
            return this.getMainFrameClasses;
        },

        accountInfoShouldBeVisible() {
            let isAdmin = this.$user.hasPermission('administration');
            let isPatient = this.$user.hasRole('Patient');

            return isAdmin || !isPatient;
        },
    }
}
</script>

<style lang="scss">
@use "../../../scss/mixins";

.frame-common {
    font-size: clamp(8pt, 2vw, 12pt);
    min-height: 100vh;
    display: grid;
    grid-template-rows: auto auto 1fr auto;

    $parent: &;
    &.-test-environment {
        #{$parent}__header {
            border: 2px solid red;
        }
    }

    &__header {
        background-color: var(--header-background-color);
        color: var(--header-font-color);
        padding: var(--padding-thin) var(--padding-thin) var(--padding-thin) var(--padding);

        @include mixins.breakpoint-md {
            padding: var(--padding-thin) var(--padding);
        }

        &__title {
            &__link {
                display: grid;
                grid-template-areas:
                    "logo main"
                    "logo sub";
                grid-template-columns: auto 1fr;
                gap: var(--gap-thin);
                font-weight: bold;
                align-items: center;

                &__logo {
                    grid-area: logo;

                    --logo-dimension: 4.5em;

                    width: var(--logo-dimension);
                    height: var(--logo-dimension);

                    & svg {
                        fill: white;
                    }
                }

                &__main {
                    grid-area: main;
                    font-size: 1.5em;
                }

                &__sub {
                    grid-area: sub;
                    font-size: 0.9em;
                }
            }
        }
    }

    $root: &;

    &.-user {
        #{$root} {
            &__header {
                display: grid;
                grid-template-columns: 1fr auto;
                gap: var(--gap);

                &__user {
                    display: grid;
                    grid-template-columns: 1fr 1fr auto auto;
                    gap: var(--gap-thin);
                    align-items: center;

                    &__info {
                        border: {
                            right: 2px solid var(--color-biscuit);
                        }
                        padding: {
                            right: var(--gap-thin);
                        }

                        &__name {

                        }

                        &__email {
                            font-size: 0.8em;

                            @include mixins.breakpoint-md {
                                font-size: 0.9em;
                            }
                        }
                    }

                    &__avatar {
                        height: 2em;
                        display: none;

                        @include mixins.breakpoint-md {
                            display: block;
                        }

                        svg {
                            width: 100%;
                            height: 100%;
                            fill: var(--header-user-fill-color);
                        }
                    }

                    &__logout {
                        &__timer {
                            font-family: monospace;
                        }

                        a:after {
                            --image-dimension-height: 3em;
                            --image-dimension-width: 2em;

                            display: block;
                            content: '';
                            height: var(--image-dimension-height);
                            width: var(--image-dimension-width);
                            background-color: var(--header-user-fill-color);
                            mask: {
                                image: url("@media/images/vector/exit.svg");
                                size: contain;
                                repeat: no-repeat;
                                position: center;
                            };
                        }

                        @include mixins.breakpoint-md {
                            margin-left: var(--margin);
                        }
                    }
                }
            }
        }
    }

    &__navigation {
        padding: var(--gap-thin);
        display: grid;
        grid-template-columns: 1fr auto;
        gap: var(--gap);

        & li {
            a {
                background-color: var(--navigation-background-color);
                color: var(--navigation-font-color);
                padding: 0.5em 1em;
                transform: translateY(calc(var(--gap-thin) * -1));
                border-bottom-right-radius: 1em;
                border-bottom-left-radius: 1em;

                &.router-link-active {
                    background-color: var(--navigation-background-color-active);
                    font-weight: bold;
                }
            }
        }
    }

    &__content {
        height: 100%;

        &__view {
            height: 100%;
            padding: var(--padding);
        }
    }

    .timeout-popup {
        display: grid;
        gap: var(--gap);
        align-items: start;
        grid-template-columns: 1fr 0.9em;

        &__cross {
            $size: 0.9em;

            height: $size;
            width: $size;
            background-color: var(--color-silver);
            mask: {
                position: center;
                repeat: no-repeat;
                size: contain;
                image: url("@media/images/vector/cross-diagonal.svg");
            };
            cursor: pointer;
            margin-top: 4px;
        }
    }
}
</style>
